import { useModal } from "@newsela/angelou";
import {
  createContext,
  Dispatch,
  DispatchWithoutAction,
  Ref,
  RefObject,
  SetStateAction,
  useContext,
  type ReactNode,
} from "react";

import useAssignmentReducer, {
  DEFAULT_STATE,
  LEARNER_DEFAULT_ASSIGNMENT,
} from "@/assignments/useAssignmentReducer";
import {
  Assignment,
  AssignmentConfig,
} from "@/types/models/Assignment.interface";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";

interface AssignmentReducerProviderProps {
  children: ReactNode;
}

export interface UpdatedAssignment
  extends Omit<Assignment, "assignment_config"> {
  activity_members?: Array<{ content_id?: string }>;
  activity_element_selections?: string[];
  assignment_config: AssignmentConfig;
}

export interface AssignModalProps {
  assignModalTriggerRef: Ref<HTMLButtonElement>;
  setIsAssignModalActive: Dispatch<SetStateAction<boolean>>;
  assignModalProps: {
    isActive: boolean;
    onEsc: () => undefined;
    closeModal: () => undefined;
    triggerRef: RefObject<HTMLElement>;
  };
}
interface IAssignmentReducerContext {
  updatedAssignment: undefined | UpdatedAssignment;
  dispatch: DispatchWithoutAction;
  assignModal: AssignModalProps;
}

export const AssignmentReducerContext = createContext(
  {} as IAssignmentReducerContext,
);

const useAssignmentReducerContext = () => {
  const assignmentReducerCtx = useContext(AssignmentReducerContext);

  if (assignmentReducerCtx === undefined) {
    throw new Error(
      "useAssignmentReducerContext must be used within an AssignmentReducerProvider.",
    );
  }

  return assignmentReducerCtx;
};

export const AssignmentReducerProvider = ({
  children,
}: AssignmentReducerProviderProps) => {
  const [updatedAssignment, dispatch] = useAssignmentReducer(DEFAULT_STATE);
  const [assignModalProps, setIsAssignModalActive, assignModalTriggerRef] =
    useModal(false);

  const { data: user } = useUser();
  const { isTeacher } = getUserProperties(user, {
    shouldUseNewUserApi: false,
  });

  const isEmptyAssignment = !isTeacher && !updatedAssignment?.content_id;

  const assignModal = {
    assignModalProps,
    assignModalTriggerRef,
    setIsAssignModalActive,
  };

  const contextData = {
    assignModal,
    updatedAssignment: isEmptyAssignment
      ? LEARNER_DEFAULT_ASSIGNMENT
      : updatedAssignment,
    dispatch,
  };

  return (
    <AssignmentReducerContext.Provider value={contextData}>
      {children}
    </AssignmentReducerContext.Provider>
  );
};

export default useAssignmentReducerContext;
